import {z} from 'zod';

import {IsoUtcDateTimeSchema, UuidV4Schema} from './common.js';

/**
 * Contratos do sistema editorial.
 *
 * Três coisas aqui não são preferência de estilo, são cerca:
 *
 * 1. **Saída de modelo é entrada não confiável.** Nada que um gerador produz
 *    entra no domínio sem passar por um destes schemas.
 * 2. **Afirmação quantitativa exige evidência.** O vínculo é obrigatório no
 *    schema, e não uma checagem que alguém lembra de fazer.
 * 3. **Caso identificado exige autorização.** O schema recusa a combinação
 *    proibida, então ela não depende de disciplina de quem chama.
 */

// ---------------------------------------------------------------------------
// Vocabulário editorial
// ---------------------------------------------------------------------------

export const AUDIENCES = [
  'smb_general',
  'retail_ecommerce',
  'professional_services',
] as const;
export const AudienceSchema = z.enum(AUDIENCES);
export type Audience = z.infer<typeof AudienceSchema>;

/** As três séries aprovadas. A ordem é a da especificação. */
export const EDITORIAL_SERIES = [
  'operational_impact',
  'automation_notebook',
  'ai_radar',
] as const;
export const EditorialSeriesSchema = z.enum(EDITORIAL_SERIES);
export type EditorialSeries = z.infer<typeof EditorialSeriesSchema>;

/**
 * As variantes de composição, uma por posição do slot no dia.
 *
 * A grade repete série DENTRO do mesmo dia por desenho: o alocador põe
 * `operational_impact` no primeiro e no terceiro slot de TODOS os 30 dias (ver
 * `allocateSeries`, em `packages/domain`). Escolher o desenho só pela série,
 * então, faz duas das três peças do dia saírem com a mesma cara — que foi o que
 * o dono viu em 2026-09-02, com as seis publicações do dia idênticas.
 *
 * A variante é a segunda coordenada do desenho: a série diz QUAL template, a
 * variante diz COM QUAL paleta e arranjo. São três porque o dia tem três slots,
 * e a igualdade não é coincidência — é o que faz slots diferentes do mesmo dia
 * caírem em variantes diferentes. `SLOTS_PER_DAY` e este vocabulário andam
 * juntos, e o teste da regra em `packages/domain` cobra isso.
 *
 * A ordem é a das posições do dia: primeiro slot, segundo, terceiro.
 */
export const PIECE_VARIANTS = ['padrao', 'invertida', 'destaque'] as const;
export const PieceVariantSchema = z.enum(PIECE_VARIANTS);
export type PieceVariant = z.infer<typeof PieceVariantSchema>;

export const CtaObjectiveSchema = z.enum(['engagement', 'mapa', 'whatsapp']);
export type CtaObjective = z.infer<typeof CtaObjectiveSchema>;

/**
 * Para qual peça a pauta serve.
 *
 * Existe porque o dono mediu, em 2026-09-03, que o Reel e a Story do mesmo
 * horário publicavam basicamente o mesmo assunto: os dois nasciam da MESMA
 * pauta, então pedir dois textos era pedir duas versões da mesma coisa. A
 * decisão foi dar à Story um banco de pauta só dela, de curiosidade e pergunta
 * sobre IA, em peça curta que fecha perguntando alguma coisa.
 *
 * `reel` é o padrão e cobre tudo que já existia: a coluna do banco nasceu com
 * ele em `default`, então nenhuma pauta semeada antes precisou ser tocada.
 *
 * `news` entrou em 2026-09-05, com o post de foto. Ela é de outra natureza: as
 * duas primeiras são inventário CURADO, escolhido por alguém que leu e achou
 * bom, e a terceira é FLUXO, que chega todo dia de páginas oficiais e vence em
 * uma semana. Por isso ela é sempre `radar`, e o banco cobra isso
 * (`topics_news_is_radar`, na migração 0076): notícia durável seria notícia que
 * não envelhece, e ela disputaria o item do dia com as notícias de verdade para
 * sempre.
 *
 * A ordem é a da escolha: o Reel só olha `reel`, a Story olha `story` primeiro
 * e `reel` como degrau de reserva, e a peça de foto só olha `news`.
 */
export const TOPIC_FORMATS = ['reel', 'story', 'news', 'carousel'] as const;
export const TopicFormatSchema = z.enum(TOPIC_FORMATS);
export type TopicFormat = z.infer<typeof TopicFormatSchema>;

/**
 * De onde a prova veio.
 *
 * `platform_api` entrou em 2026-09-05, com o radar de virais, e ela existe
 * separada de `primary_external` porque a ORIGEM e outra, e nao porque fica
 * bonito ter mais um valor.
 *
 * `primary_external` e a captura de uma PAGINA, feita por `fetchSource`, que le
 * a URL publica e tira o sha256 do corpo. instagram.com nao e capturavel assim:
 * a pagina exige login e devolve a parede. Reusar aquele tipo faria o portao
 * prometer uma captura que nao aconteceu, e o sha256 seria de um HTML de
 * login.
 *
 * `platform_api` diz a verdade sobre a origem: quem afirmou o numero foi a
 * PLATAFORMA que hospeda o post, pela API dela, para a nossa conta. O snapshot
 * guarda o JSON que voltou, o permalink e a data.
 */
export const ProofTypeSchema = z.enum([
  'aiva_demo',
  'anonymous_case',
  'identified_case',
  'simulation',
  'primary_external',
  'platform_api',
]);
export type ProofType = z.infer<typeof ProofTypeSchema>;

/** Os sete portões, com os nomes que o servidor usa. */
export const GateNameSchema = z.enum([
  'source',
  'claims',
  'confidentiality',
  'originality',
  'brand',
  'rights',
  'cta',
]);
export type GateName = z.infer<typeof GateNameSchema>;

export const EditorialGateOutcomeSchema = z.enum(['pass', 'alert', 'block']);
export type EditorialGateOutcome = z.infer<typeof EditorialGateOutcomeSchema>;

/**
 * Estados editoriais. `released` é o fim da linha do editorial: quem publica é o
 * plano `03`, e ele não volta para cá.
 */
export const EditorialContentStatusSchema = z.enum([
  'draft',
  'quality_check',
  'awaiting_approval',
  'blocked',
  'changes_requested',
  'rejected',
  'released',
]);
export type EditorialContentStatus = z.infer<
  typeof EditorialContentStatusSchema
>;

/**
 * O que uma peça É, e por que agora são quatro.
 *
 * `reel` e `story` são as DUAS peças de um item da grade: mesmo horário, mesma
 * combinação de série, público e objetivo, pauta própria desde a migração 0074.
 *
 * `photo` é a peça ÚNICA do item de notícia, que entrou em 2026-09-05 com o
 * pedido do dono: uma publicação de FEED em imagem, uma por dia, às 12:00, com
 * foto realista gerada por IA e legenda informativa sobre uma notícia recente.
 *
 * Ela não é um Reel com foto no lugar do vídeo. Três coisas mudam por ser outra
 * espécie, e cada uma tem cerca própria:
 *
 * 1. **A mídia é a imagem**, e não sai do renderizador. O portão de marca ganha
 *    um caminho que mede a IMAGEM, porque não existe relatório de composição.
 * 2. **O contêiner da Meta é `IMAGE` com `image_url`**, e não `REELS` com
 *    `video_url`.
 * 3. **A pauta é de formato `news`**, e a evidência dela precisa de data de
 *    publicação recente.
 */
export const PIECE_KINDS = ['reel', 'story', 'photo', 'carousel'] as const;
export const PieceKindSchema = z.enum(PIECE_KINDS);
export type PieceKind = z.infer<typeof PieceKindSchema>;

/**
 * O CARROSSEL, que entrou em 2026-09-06 com o pedido do dono.
 *
 * "A grade diária deve ter: 3 Reels; 3 Stories; 1 foto; 1 carrossel. São 8
 * peças por dia. O carrossel é adicional às postagens existentes."
 *
 * Ele é uma quarta espécie e não um Reel de imagens paradas, porque três coisas
 * mudam por ser outra espécie:
 *
 * 1. **A mídia é um CONJUNTO ORDENADO de sete JPEG**, e não um arquivo. A
 *    ordem faz parte do que o dono aprova: trocar o slide 4 de lugar é publicar
 *    outra peça.
 * 2. **O contêiner da Meta é `CAROUSEL` com `children`**, montado depois de
 *    sete contêineres filhos marcados `is_carousel_item`. São nove chamadas
 *    onde as outras espécies fazem duas.
 * 3. **Um slide bloqueado impede a peça inteira.** Não existe publicar os três
 *    primeiros: metade de um carrossel não é meia peça, é outra peça.
 *
 * O que ele NÃO muda: continua sendo UMA peça, UMA versão, UMA aprovação e UM
 * post. Os sete slides são partes ordenadas dela, e não sete peças.
 */
export const CAROUSEL_SLIDES = 7;

/**
 * O que um slide É, e a lista mora no CONTRATO desde 2026-09-07.
 *
 * Ela existia em três lugares — o schema do renderizador, o template e
 * `FUNCOES_DO_SLIDE`, no domínio — e as três eram cópias da mesma frase do
 * dono ("capa, 5 dicas práticas e fechamento com um CTA"). Uma quarta cópia
 * nasceria agora, no pedido de imagem por slide, e é o tipo de lista que
 * diverge sem barulho: no dia da divergência o gerador de imagem pediria a
 * cena de uma função que o template não desenha.
 *
 * Aqui porque o contrato é o único pacote que os outros três já importam.
 */
export const CAROUSEL_SLIDE_ROLES = ['cover', 'tip', 'closing'] as const;
export const CarouselSlideRoleSchema = z.enum(CAROUSEL_SLIDE_ROLES);
export type CarouselSlideRole = z.infer<typeof CarouselSlideRoleSchema>;

/**
 * O horário local do item de carrossel, e por que ele é constante.
 *
 * 16:30, e o número saiu de uma conferência contra a grade que já existe, e não
 * de preferência: o dia tem os três horários da grade (09:00, 14:00 e 19:00 na
 * configuração vigente) e o item de notícia às 12:00. 16:30 é o único dos
 * horários propostos que não encosta em nenhum deles, e ele cai no meio da
 * tarde, entre a peça das 14:00 e a das 19:00.
 *
 * Ele é CONSTANTE pelo mesmo motivo de `NEWS_ITEM_TIME`: a unicidade de
 * `(ciclo, data local, horário)` depende de ele não colidir com `dailyTimes`, e
 * `CreateEditorialCycleRequestSchema` recusa este horário entre os três
 * exatamente por isso. Duas variáveis para a mesma regra divergiriam, e no dia
 * da divergência o carrossel sumiria em silêncio dentro de um `insert`
 * recusado.
 */
export const CAROUSEL_ITEM_TIME = '16:30:00';

/**
 * O horário local do item de notícia, e por que ele é constante.
 *
 * Meio-dia: fora dos três horários da grade (09:00, 14:00, 19:00) e no meio do
 * dia de quem trabalha, que é quando notícia se lê.
 *
 * Ele é CONSTANTE e não configurável porque a unicidade de
 * `(ciclo, data local, horário)` depende de ele não colidir com `dailyTimes`, e
 * `CreateEditorialCycleRequestSchema` recusa este horário entre os três
 * exatamente por isso. Duas variáveis para a mesma regra divergiriam, e no dia
 * da divergência o item de notícia sumiria em silêncio dentro de um `insert`
 * recusado.
 */
export const NEWS_ITEM_TIME = '12:00:00';

// ---------------------------------------------------------------------------
// Ciclo editorial
// ---------------------------------------------------------------------------

const TimeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/u, 'use HH:mm:ss');

/** O fuso tem que existir de verdade, e quem sabe isso é a base IANA. */
const TimezoneSchema = z
  .string()
  .min(1)
  .refine(
    (value) => {
      try {
        new Intl.DateTimeFormat('en-US', {timeZone: value});
        return true;
      } catch {
        return false;
      }
    },
    {message: 'fuso horário desconhecido na base IANA'},
  );

export const CreateEditorialCycleRequestSchema = z.strictObject({
  startsOn: z.iso.date(),
  timezone: TimezoneSchema,
  /**
   * Exatamente três horários, distintos. O lote diário é 3 Reels e 3 Stories,
   * um par por horário; horário repetido faria duas peças disputarem o mesmo
   * slot e uma delas sumiria sem ninguém notar.
   */
  dailyTimes: z
    .tuple([TimeOfDaySchema, TimeOfDaySchema, TimeOfDaySchema])
    .refine((times) => new Set(times).size === times.length, {
      message: 'os três horários diários precisam ser distintos',
    })
    /*
     * O horário do item de notícia é RESERVADO, e a recusa é aqui.
     *
     * `content_items` é único por `(ciclo, data local, horário)`. O item de
     * notícia nasce às 12:00 junto com a grade, na mesma transação: um ciclo
     * com 12:00 entre os três horários faria a abertura inteira falhar por
     * violação de unicidade, com uma mensagem do banco que não explica nada,
     * depois de 90 itens já escritos e desfeitos.
     *
     * Recusar AQUI custa uma mensagem legível ao dono, antes de qualquer
     * escrita. Aceitar e deixar o banco reclamar seria a cerca funcionando pelo
     * motivo certo e no lugar errado.
     */
    .refine((times) => !times.includes(NEWS_ITEM_TIME), {
      message:
        `${NEWS_ITEM_TIME} é o horário do post de foto do dia e não pode ` +
        'estar entre os três horários da grade',
    })
    /*
     * O horário do carrossel é RESERVADO pelo MESMO motivo do da notícia.
     *
     * Uma cerca por horário reservado, e não uma lista genérica, porque a
     * mensagem precisa dizer QUAL peça ocupa aquele horário: "16:30:00 já está
     * ocupado" manda o operador procurar num banco que ele não tem acesso.
     */
    .refine((times) => !times.includes(CAROUSEL_ITEM_TIME), {
      message:
        `${CAROUSEL_ITEM_TIME} é o horário do carrossel do dia e não pode ` +
        'estar entre os três horários da grade',
    }),
});
export type CreateEditorialCycleRequest = z.infer<
  typeof CreateEditorialCycleRequestSchema
>;

export const EditorialCycleSchema = z.strictObject({
  id: UuidV4Schema,
  startsOn: z.iso.date(),
  endsOn: z.iso.date(),
  timezone: TimezoneSchema,
  dailyTimes: z.tuple([TimeOfDaySchema, TimeOfDaySchema, TimeOfDaySchema]),
  status: z.enum(['draft', 'active', 'completed', 'cancelled']),
  createdAt: IsoUtcDateTimeSchema,
});
export type EditorialCycleDto = z.infer<typeof EditorialCycleSchema>;

/**
 * A resposta da abertura do ciclo, com o tamanho da grade que nasceu.
 *
 * Os dois contadores existem porque quem abre um ciclo precisa CONFERIR o que
 * abriu: 90 itens e 180 peças é o ciclo inteiro, e qualquer outro número é
 * grade pela metade. Sem eles, a única forma de saber seria consultar o banco
 * na mão depois de uma escrita de 270 linhas.
 */
export const CreateEditorialCycleResponseSchema = EditorialCycleSchema.extend({
  itemCount: z.int().nonnegative(),
  pieceCount: z.int().nonnegative(),
  /**
   * Os itens de NOTÍCIA: um por dia, e portanto 30 num ciclo cheio.
   *
   * Ele é um terceiro contador pelo mesmo motivo dos dois primeiros: quem abre
   * o ciclo precisa CONFERIR o que abriu, e 90 itens de grade com zero itens de
   * notícia é um mês inteiro sem post de foto que só apareceria no dia seguinte,
   * no painel, com o dono contando seis cartões em vez de sete.
   */
  newsItemCount: z.int().nonnegative(),
  /**
   * Os itens de CARROSSEL: um por dia, e portanto 30 num ciclo cheio.
   *
   * Quarto contador pelo mesmo motivo dos três primeiros: um ciclo com 90 itens
   * de grade, 30 de notícia e zero de carrossel é um mês inteiro sem carrossel,
   * e isso só apareceria no dia seguinte, no painel, com o dono contando sete
   * cartões em vez de oito.
   */
  carouselItemCount: z.int().nonnegative(),
});
export type CreateEditorialCycleResponse = z.infer<
  typeof CreateEditorialCycleResponseSchema
>;

// ---------------------------------------------------------------------------
// Pauta
// ---------------------------------------------------------------------------

export const CreateTopicRequestSchema = z.strictObject({
  title: z.string().trim().min(1).max(200),
  premise: z.string().trim().min(1).max(2_000),
  audience: AudienceSchema,
  series: EditorialSeriesSchema,
  objective: CtaObjectiveSchema,
  /** `durable` vale por muito tempo; `radar` vence e precisa de data. */
  sourceMode: z.enum(['durable', 'radar']),
  tags: z.array(z.string().trim().min(1).max(40)).max(10),
});
export type CreateTopicRequest = z.infer<typeof CreateTopicRequestSchema>;

export const TopicSchema = CreateTopicRequestSchema.extend({
  id: UuidV4Schema,
  /**
   * Fica no schema de LEITURA e fora do de criação de propósito.
   *
   * Quem escreve pauta hoje é a semeadura, e lá o campo é `formato`, no
   * envelope em português, ao lado de `expiraEm` — pelo mesmo motivo que ele:
   * quem grava é `createTopic`, e não uma rota. Aceitá-lo também aqui dentro
   * daria duas formas de dizer a mesma coisa no mesmo arquivo, e no dia em que
   * as duas divergissem uma delas ganharia em silêncio.
   */
  format: TopicFormatSchema,
  status: z.enum(['eligible', 'withdrawn', 'expired']),
  expiresAt: IsoUtcDateTimeSchema.nullable(),
  createdAt: IsoUtcDateTimeSchema,
});
export type TopicDto = z.infer<typeof TopicSchema>;

// ---------------------------------------------------------------------------
// Fonte e evidência
// ---------------------------------------------------------------------------

/** Só http e https. `javascript:` e `data:` não são fonte, são vetor. */
const HttpUrlSchema = z.url().refine((value) => /^https?:\/\//u.test(value), {
  message: 'a fonte precisa ser http ou https',
});

export const CreateSourceRequestSchema = z
  .strictObject({
    kind: z.enum(['url', 'uploaded_file', 'client_authorization']),
    canonicalUrl: HttpUrlSchema.nullable(),
    evidenceObjectId: z.string().min(1).nullable(),
    title: z.string().trim().min(1).max(300),
    author: z.string().trim().min(1).max(200).nullable(),
    publishedAt: IsoUtcDateTimeSchema.nullable(),
    license: z.string().trim().min(1).max(200).nullable(),
    isPrimary: z.boolean(),
  })
  .refine((source) => source.kind !== 'url' || source.canonicalUrl !== null, {
    message: 'fonte do tipo url precisa de canonicalUrl',
    path: ['canonicalUrl'],
  })
  .refine(
    (source) => source.kind === 'url' || source.evidenceObjectId !== null,
    {
      message: 'fonte que não é url precisa do objeto de evidência guardado',
      path: ['evidenceObjectId'],
    },
  );
export type CreateSourceRequest = z.infer<typeof CreateSourceRequestSchema>;

export const SourceSchema = z.strictObject({
  id: UuidV4Schema,
  kind: z.enum(['url', 'uploaded_file', 'client_authorization']),
  canonicalUrl: HttpUrlSchema.nullable(),
  evidenceObjectId: z.string().min(1).nullable(),
  title: z.string().min(1),
  author: z.string().min(1).nullable(),
  publishedAt: IsoUtcDateTimeSchema.nullable(),
  license: z.string().min(1).nullable(),
  isPrimary: z.boolean(),
  status: z.enum(['active', 'withdrawn']),
  createdAt: IsoUtcDateTimeSchema,
});
export type SourceDto = z.infer<typeof SourceSchema>;

/** Prova que fala de um cliente com nome exige autorização dele. */
const IDENTIFIED_PROOFS = new Set<ProofType>(['identified_case']);

/**
 * A forma do pedido, sem a regra cruzada.
 *
 * Existe separada porque a API precisa distinguir "não mandou autorização" de
 * "mandou uma autorização revogada", e as duas recusas têm o mesmo código de
 * negócio. Validar a regra aqui devolveria erro de validação genérico para o
 * primeiro caso e erro de negócio para o segundo, sendo que são a mesma falta.
 */
export const EvidenceRequestShapeSchema = z.strictObject({
  sourceId: UuidV4Schema,
  summary: z.string().trim().min(1).max(2_000),
  proofType: ProofTypeSchema,
  clientAuthorizationId: UuidV4Schema.nullable(),
});

export const CreateEvidenceRequestSchema = EvidenceRequestShapeSchema.refine(
  (evidence) =>
    !IDENTIFIED_PROOFS.has(evidence.proofType) ||
    evidence.clientAuthorizationId !== null,
  {
    message: 'caso identificado exige autorização específica do cliente',
    path: ['clientAuthorizationId'],
  },
);
export type CreateEvidenceRequest = z.infer<typeof CreateEvidenceRequestSchema>;

export const EvidenceSnapshotSchema = z.strictObject({
  id: UuidV4Schema,
  sourceId: UuidV4Schema,
  title: z.string().min(1),
  author: z.string().min(1).nullable(),
  publishedAt: IsoUtcDateTimeSchema.nullable(),
  retrievedAt: IsoUtcDateTimeSchema,
  summary: z.string().min(1),
  proofType: ProofTypeSchema,
  sha256: z.string().regex(/^[0-9a-f]{64}$/u),
  license: z.string().min(1).nullable(),
  authorizationState: z.enum(['not_required', 'valid', 'revoked']),
  expiresAt: IsoUtcDateTimeSchema.nullable(),
});
export type EvidenceSnapshotDto = z.infer<typeof EvidenceSnapshotSchema>;

// ---------------------------------------------------------------------------
// Versão de conteúdo
// ---------------------------------------------------------------------------

export const EditorialGateResultSchema = z.strictObject({
  gate: GateNameSchema,
  outcome: EditorialGateOutcomeSchema,
  policyVersionId: UuidV4Schema,
  code: z.string().min(1),
  details: z.record(z.string(), z.unknown()),
});
export type EditorialGateResultDto = z.infer<typeof EditorialGateResultSchema>;

export const ContentVersionSchema = z.strictObject({
  id: UuidV4Schema,
  contentPieceId: UuidV4Schema,
  contentItemId: UuidV4Schema,
  version: z.int().positive(),
  status: EditorialContentStatusSchema,
  pieceKind: PieceKindSchema,
  manifestHash: z
    .string()
    .regex(/^[0-9a-f]{64}$/u)
    .nullable(),
  createdAt: IsoUtcDateTimeSchema,
});
export type ContentVersionDto = z.infer<typeof ContentVersionSchema>;

export const ContentHistorySchema = z.strictObject({
  version: z.int().positive(),
  action: z.enum([
    'generated',
    'regenerated',
    'approved',
    'changes_requested',
    'rejected',
    'invalidated',
  ]),
  actor: z.enum(['system', 'owner']),
  reason: z.string().min(1).nullable(),
  occurredAt: IsoUtcDateTimeSchema,
});
export type ContentHistoryDto = z.infer<typeof ContentHistorySchema>;

export const ContentVersionDetailSchema = ContentVersionSchema.extend({
  script: z.string().min(1),
  caption: z.string().min(1),
  cta: z.string().min(1),
  /** Rota autenticada e curta. Nunca URL assinada de bucket. */
  previewUrl: z.string().min(1).nullable(),
  evidence: z.array(EvidenceSnapshotSchema),
  gates: z.array(EditorialGateResultSchema),
  history: z.array(ContentHistorySchema),
});
export type ContentVersionDetailDto = z.infer<
  typeof ContentVersionDetailSchema
>;

export const ApprovalCardSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  contentPieceId: UuidV4Schema,
  contentItemId: UuidV4Schema,
  version: z.int().positive(),
  status: EditorialContentStatusSchema,
  pieceKind: PieceKindSchema,
  series: EditorialSeriesSchema,
  audience: AudienceSchema,
  objective: CtaObjectiveSchema,
  previewUrl: z.string().min(1).nullable(),
  script: z.string().min(1),
  caption: z.string().min(1),
  cta: z.string().min(1),
  evidence: z.array(EvidenceSnapshotSchema),
  gates: z.array(EditorialGateResultSchema),
  manifestHash: z
    .string()
    .regex(/^[0-9a-f]{64}$/u)
    .nullable(),
  scheduledAt: IsoUtcDateTimeSchema,
  history: z.array(ContentHistorySchema),
});
export type ApprovalCardDto = z.infer<typeof ApprovalCardSchema>;

// ---------------------------------------------------------------------------
// Decisões
// ---------------------------------------------------------------------------

/**
 * A régua de todo texto que o dono escreve para justificar uma decisão.
 *
 * Exportada porque as rotas do painel (`panel.ts`) precisam da MESMA régua que
 * o serviço cobra em `validarComentario`. Uma cópia lá e outra aqui divergiriam
 * no dia em que o mínimo mudasse, e a tela passaria a mandar texto que o
 * domínio recusa, ou a recusar texto que o domínio aceitaria.
 */
export const DecisionCommentSchema = z.string().trim().min(3).max(1_000);
const ExpectedVersionSchema = z.int().positive();

/**
 * A ressalva escrita de quem aprova peça com alerta.
 *
 * Mesma régua do comentário de decisão, mais o `null` de "não há alerta, e
 * portanto não há o que justificar". Exportada porque a rota do painel
 * (`ApproveContentInputSchema`, em `panel.ts`) precisa da MESMA régua que o
 * serviço de aprovação cobra: uma cópia lá e outra aqui divergiriam no dia em
 * que o mínimo mudasse, e o painel passaria a mandar texto que o domínio
 * recusa, ou a recusar texto que o domínio aceitaria.
 */
export const ApprovalJustificationSchema = DecisionCommentSchema.nullable();

export const RegenerateRequestSchema = z.strictObject({
  expectedVersion: ExpectedVersionSchema,
  instruction: z.string().trim().min(3).max(1_000),
  reason: DecisionCommentSchema,
});
export type RegenerateRequest = z.infer<typeof RegenerateRequestSchema>;

/**
 * A aprovação carrega o hash que o revisor VIU. Se a versão mudou entre a tela
 * e o clique, o hash não bate e a aprovação é recusada, em vez de liberar uma
 * peça que ninguém leu.
 */
export const ApproveContentRequestSchema = z.strictObject({
  expectedManifestHash: z.string().regex(/^[0-9a-f]{64}$/u),
  justification: ApprovalJustificationSchema,
});
export type ApproveContentRequest = z.infer<typeof ApproveContentRequestSchema>;

export const RequestChangesRequestSchema = z.strictObject({
  expectedVersion: ExpectedVersionSchema,
  comment: DecisionCommentSchema,
});
export type RequestChangesRequest = z.infer<typeof RequestChangesRequestSchema>;

export const RejectContentRequestSchema = z.strictObject({
  expectedVersion: ExpectedVersionSchema,
  comment: DecisionCommentSchema,
});
export type RejectContentRequest = z.infer<typeof RejectContentRequestSchema>;

export const ApprovalDecisionSchema = z.strictObject({
  id: UuidV4Schema,
  contentVersionId: UuidV4Schema,
  decision: z.enum(['approved', 'changes_requested', 'rejected']),
  manifestHash: z
    .string()
    .regex(/^[0-9a-f]{64}$/u)
    .nullable(),
  releaseSlotId: UuidV4Schema.nullable(),
  comment: z.string().min(1).nullable(),
  decidedAt: IsoUtcDateTimeSchema,
});
export type ApprovalDecisionDto = z.infer<typeof ApprovalDecisionSchema>;

export const EditorialRunSchema = z.strictObject({
  runId: UuidV4Schema,
  accountId: UuidV4Schema,
  localDate: z.iso.date(),
  status: z.enum(['queued', 'running', 'partial', 'ready', 'failed']),
  readyPieces: z.int().nonnegative(),
  blockedPieces: z.int().nonnegative(),
  failedPieces: z.int().nonnegative(),
  startedAt: IsoUtcDateTimeSchema.nullable(),
  finishedAt: IsoUtcDateTimeSchema.nullable(),
});
export type EditorialRunDto = z.infer<typeof EditorialRunSchema>;
